import { XButton } from '@adornis/chemistry/elements/components/x-button.js';
import { customElement } from 'lit/decorators.js';
import { baseFontSize, crSpacing } from '../theme.js';
import type { Styles } from '@adornis/ass/style.js';

/**
 * @customElement cr-button
 */
@customElement('cr-button')
export class ChromiumButton extends XButton {
  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host([condensed])': {
          maxWidth: '80px',
        },
        ':host([mode=action])': {
          background: this.colors.secondary,
          color: 'white',
          borderRadius: '2rem',
          transition: 'all .25s ease',
          textTransform: 'uppercase',
          minWidth: '10rem',
          justifyContent: 'center',
        },
        ':host([mode=action][primary])': {
          background: this.colors.primary,
          color: this.colors.on.primary,
          border: 'none',
        },
        ':host([mode=action]:hover)': {
          background: '#f69b1b',
        },
        ':host([mode=action]:active)': { boxShadow: 'none' },
        ':host([mode=action][tone=error])': { background: this.colors.tone.error, color: this.colors.on.tone.error },

        ':host([mode=text])': {
          transition: 'all 0.2s ease-out',
          background: 'transparent',
          color: 'unset',
          border: `2px solid transparent`,
          // textDecoration: 'underline',
          '#innerwrapper': {
            padding: '4px',
          },
          'cr-link': { padding: 0 },
        },
        ':host([mode=icon]:hover)': {
          transition: 'all 0.1s ease-in',
          opacity: 0.6,
        },
        ':host([mode=text]:hover)': {
          transition: 'all 0.1s ease-in',
          opacity: 0.6,
          borderRadius: '0',
        },
        ':host([mode=text][tone=error])': { color: this.colors.tone.error },

        ':host([mode=text-narrow])': {
          transition: 'all 0.2s ease-out',
          background: 'transparent',
          color: 'unset',
          border: `2px solid transparent`,
          '#innerwrapper': {
            alignItems: 'start',
          },
          'cr-link': { padding: 0 },
        },
        ':host([mode=text-narrow]:hover)': {
          transition: 'all 0.1s ease-in',
          opacity: 0.6,
          borderRadius: '0',
        },
        ':host([mode=text-narrow][tone=error])': { color: this.colors.tone.error },

        ':host([mode=outline])': {
          background: 'transparent',
          border: `1px solid ${this.colors.secondary}`,
          color: this.colors.secondary,
          textTransform: 'uppercase',
        },
        ':host([mode=outline][tone=error])': { border: `1px solid #FF4B5B`, color: '#FF4B5B' },

        ':host([mode=fab])': {
          transition: 'all .25s ease-out',
          backgroundImage: `linear-gradient(to left, ${this.colors.secondary} 0%, ${this.colors.secondary} 25%, ${this.colors.primary} 75%, ${this.colors.primary} 100%)`,
          backgroundPosition: '100% 0',
          backgroundSize: '1000% 100%',
          color: this.colors.on.secondary,
          justifyContent: 'center',
          height: '50px',
          width: '50px',

          borderRadius: '2rem',
          '#innerwrapper': {
            height: '50px',
            width: '50px',
            justifyContent: 'center',
            alignItems: 'center',
          },
          'cr-flex ': {
            'cr-icon': {
              transform: 'translateX(4px)',
              transition: 'transform .25s ease-out',
              color: this.colors.white,
              fontSize: '1.8em !important',
            },
          },
        },
        ':host([mode=fab][over-animation=jump]:hover)': {
          'cr-icon': {
            transform: 'translateX(10px)',
          },
        },
        ':host([mode=fab]:hover)': {
          backgroundPosition: '0 0',
        },

        ':host([mode=fab][small])': {
          transition: 'all .25s ease-out',
          backgroundColor: this.colors.secondary,
          backgroundImage: `linear-gradient(to left, ${this.colors.secondary} 0%, ${this.colors.secondary} 25%, ${this.colors.primary} 75%, ${this.colors.primary} 100%)`,
          backgroundPosition: '100% 0',
          backgroundSize: '1000% 100%',
          color: this.colors.on.secondary,
          borderRadius: '2rem',
          height: '30px',
          width: '30px',
          justifyContent: 'center',
          '#innerwrapper': {
            height: '30px',
            width: '30px',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0',
          },
          'cr-flex ': {
            'cr-icon': {
              transform: 'translateX(3.5px)',
              transition: 'transform .25s ease-out',
              color: this.colors.white,
              fontSize: '1.1em !important',
            },
          },
        },

        ':host([mode=fab][tone=checked])': { background: this.colors.primary },

        ':host([mode=fab][tone=error])': { background: this.colors.tone.error, color: this.colors.on.tone.error },

        ':host([mode=icon])': {
          background: 'transparent',
          color: this.colors.dark,
          borderRadius: '2rem',
          '#innerwrapper': {
            height: '25px',
            width: '25px',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0',
          },
          'cr-icon': {
            fontSize: '22px',
          },
        },
        ':host([mode=icon][big])': {
          '#innerwrapper': { height: '35px', width: '35px' },
          'cr-icon': { fontSize: '22px !important' },
        },
        ':host([mode=icon][active])': {
          background: this.colors.accent,
          color: this.colors.on.accent,
        },

        ':host': {
          display: 'inline-block',
          borderRadius: `${baseFontSize}px`,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          cursor: 'pointer',
          textAlign: 'center',
          '#innerwrapper': { padding: `${crSpacing.sm} ${crSpacing.sm}` },
        },

        ':host([disabled])': { opacity: '.5', pointerEvents: 'none' },
      },
    ];
  }
}
